import { useFormik, type FormikErrors } from 'formik';
import React from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { ColumnEditModal } from './ColumnEditModal';
import { type FormValues } from './form';
import {
  type RowDisplayOption,
  type ColumnDisplayOptions,
} from '../../../export';

type CustomContentOption = {
  id: string;
  name: string;
};

type ParentCustomContentOption = CustomContentOption & {
  subItems: CustomContentOption[];
};

type Props = {
  isOpen: boolean;
  title: string;
  description: string;
  customContent?: string[] | null;
  customContentOptions: (ParentCustomContentOption | CustomContentOption)[];
  isDeletable: boolean;
  getCustomContentValueLabel: (value: string) => string;
  isStaticCustomContentValue: (value: string) => boolean;
  getCustomContentPreview?: (customContent: string[]) => string;
  onRemoveColumn: () => void;
  onClose: () => void;
  onSaveColumn: (values: FormValues) => void;
  isAnalyticalColumn: boolean;
  rowDisplayOptions?: RowDisplayOption[];
  displayOptions?: ColumnDisplayOptions;
  maxLength?: string;
};

export const ColumnEditModalContainer = ({
  title,
  customContent = null,
  displayOptions,
  maxLength,
  onSaveColumn,
  ...rest
}: Props) => {
  const { t } = useTranslation('global');

  const formikProps = useFormik<FormValues>({
    initialValues: {
      customContent: customContent || null,
      title,
      displayOptions,
      maxLength,
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};

      if (!values.title.trim()) {
        errors.title = t('exports.emptyColumnTitleError');
      }

      return errors;
    },
    onSubmit: (values) => {
      onSaveColumn({
        title: values.title,
        maxLength: values.maxLength,
        customContent: values.customContent,
        displayOptions: values.displayOptions,
      });
    },
  });
  const isCustomContent = customContent !== null;

  return (
    <ColumnEditModal
      title={title}
      isCustomContent={isCustomContent}
      {...rest}
      {...formikProps}
    />
  );
};
